import React, { useState } from 'react';
import { WalletIcon, TrendingUpIcon, AlertCircleIcon, DownloadIcon, ArrowUpRightIcon, PlaneIcon, HotelIcon, CarIcon, UtensilsIcon, ReceiptIcon, ClockIcon, CheckCircleIcon, AlertTriangleIcon, PieChartIcon, FilterIcon, GlobeIcon, HomeIcon, SettingsIcon, XIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function BudgetPage() {
  const {
    travelRequests
  } = useAppContext();
  const [period, setPeriod] = useState('Year to Date');
  const [viewMode, setViewMode] = useState<'department' | 'project' | 'type'>('department');
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const pendingRequests = travelRequests.filter(r => r.status === 'pending');
  const pendingCount = pendingRequests.length;
  const pendingAmount = pendingRequests.reduce((sum, req) => sum + req.amount, 0);
  const categories = [{
    name: 'Flights',
    amount: 156000,
    percent: 43,
    icon: PlaneIcon,
    color: 'bg-navy-700',
    text: 'text-navy-700',
    bg: 'bg-navy-50'
  }, {
    name: 'Hotels',
    amount: 98000,
    percent: 27,
    icon: HotelIcon,
    color: 'bg-indigo-500',
    text: 'text-indigo-600',
    bg: 'bg-indigo-50'
  }, {
    name: 'Ground Transport',
    amount: 52000,
    percent: 14,
    icon: CarIcon,
    color: 'bg-emerald-500',
    text: 'text-emerald-600',
    bg: 'bg-emerald-50'
  }, {
    name: 'Meals & Entertainment',
    amount: 38000,
    percent: 11,
    icon: UtensilsIcon,
    color: 'bg-gold-500',
    text: 'text-gold-600',
    bg: 'bg-gold-50'
  }, {
    name: 'Other',
    amount: 18000,
    percent: 5,
    icon: ReceiptIcon,
    color: 'bg-slate-400',
    text: 'text-slate-600',
    bg: 'bg-slate-100'
  }];
  const departments = [{
    name: 'Engineering',
    budget: 150000,
    spent: 85000,
    remaining: 65000,
    percent: 56,
    status: 'On Track',
    color: 'emerald'
  }, {
    name: 'Sales',
    budget: 200000,
    spent: 145000,
    remaining: 55000,
    percent: 72,
    status: 'Warning',
    color: 'amber'
  }, {
    name: 'Marketing',
    budget: 100000,
    spent: 45000,
    remaining: 55000,
    percent: 45,
    status: 'On Track',
    color: 'emerald'
  }, {
    name: 'Product',
    budget: 80000,
    spent: 75000,
    remaining: 5000,
    percent: 93,
    status: 'Critical',
    color: 'red'
  }, {
    name: 'HR',
    budget: 50000,
    spent: 12000,
    remaining: 38000,
    percent: 24,
    status: 'On Track',
    color: 'emerald'
  }];
  const alerts = [{
    type: 'critical',
    title: 'Product department at 93% utilization',
    desc: 'Only $5,000 remaining for Q4. Immediate review required.',
    time: '2 hours ago',
    icon: AlertCircleIcon,
    color: 'border-l-4 border-red-500 bg-red-50 text-red-800'
  }, {
    type: 'warning',
    title: 'Sales department approaching 75% threshold',
    desc: 'Review upcoming travel plans for Q4 to ensure compliance.',
    time: '1 day ago',
    icon: AlertTriangleIcon,
    color: 'border-l-4 border-amber-500 bg-amber-50 text-amber-800'
  }, {
    type: 'info',
    title: `${pendingCount} pending requests totaling $${pendingAmount.toLocaleString()}`,
    desc: 'Awaiting approval from department heads.',
    time: 'Just now',
    icon: ClockIcon,
    color: 'border-l-4 border-navy-500 bg-navy-50 text-navy-800'
  }, {
    type: 'success',
    title: 'HR department under budget by 76%',
    desc: 'Consider budget reallocation to high-utilization departments.',
    time: '3 days ago',
    icon: CheckCircleIcon,
    color: 'border-l-4 border-emerald-500 bg-emerald-50 text-emerald-800'
  }];
  return <div className="space-y-6 pb-12" data-id="element-885">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-886">
        <div data-id="element-887">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-888">
            Budget Management
          </h2>
          <p className="text-slate-500" data-id="element-889">
            Track and control corporate travel spend across departments.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-890">
          <div className="relative" data-id="element-891">
            <select value={period} onChange={e => setPeriod(e.target.value)} className="appearance-none rounded-lg border border-slate-300 bg-white px-4 py-2 pr-8 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-navy-600" data-id="element-892">
              <option data-id="element-893">This Quarter</option>
              <option data-id="element-894">Last Quarter</option>
              <option data-id="element-895">Year to Date</option>
              <option data-id="element-896">Full Year</option>
            </select>
            <FilterIcon className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-897" />
          </div>
          <button className="flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-898">
            <DownloadIcon className="mr-2 h-4 w-4" data-id="element-899" />
            Export Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-900">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-901">
          <div className="flex justify-between items-start mb-4" data-id="element-902">
            <div data-id="element-903">
              <p className="text-sm font-medium text-slate-500" data-id="element-904">
                Total Annual Budget
              </p>
              <h3 className="text-2xl font-bold text-navy-900 mt-1" data-id="element-905">
                $580,000
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-navy-50 flex items-center justify-center text-navy-700" data-id="element-906">
              <WalletIcon className="h-5 w-5" data-id="element-907" />
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-908">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-909">
              <div className="h-full bg-navy-700 rounded-full" style={{
              width: '62%'
            }} data-id="element-910" />
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-911">62%</span>
          </div>
          <p className="text-xs text-slate-500 mt-2" data-id="element-912">Utilized</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-913">
          <div className="flex justify-between items-start mb-4" data-id="element-914">
            <div data-id="element-915">
              <p className="text-sm font-medium text-slate-500" data-id="element-916">
                Spent Year-to-Date
              </p>
              <h3 className="text-2xl font-bold text-navy-900 mt-1" data-id="element-917">
                $362,000
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-918">
              <TrendingUpIcon className="h-5 w-5" data-id="element-919" />
            </div>
          </div>
          <div className="flex items-center text-sm font-medium text-emerald-600" data-id="element-920">
            <ArrowUpRightIcon className="mr-1 h-4 w-4" data-id="element-921" />
            +12% vs last year
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-922">
          <div className="flex justify-between items-start mb-4" data-id="element-923">
            <div data-id="element-924">
              <p className="text-sm font-medium text-slate-500" data-id="element-925">
                Remaining Budget
              </p>
              <h3 className="text-2xl font-bold text-navy-900 mt-1" data-id="element-926">
                $218,000
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600" data-id="element-927">
              <PieChartIcon className="h-5 w-5" data-id="element-928" />
            </div>
          </div>
          <p className="text-sm text-slate-500" data-id="element-929">Avg $72.7k/month remaining</p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-930">
          <div className="flex justify-between items-start mb-4" data-id="element-931">
            <div data-id="element-932">
              <p className="text-sm font-medium text-slate-500" data-id="element-933">
                Pending Requests
              </p>
              <h3 className="text-2xl font-bold text-navy-900 mt-1" data-id="element-934">
                ${pendingAmount.toLocaleString()}
              </h3>
            </div>
            <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-935">
              <ClockIcon className="h-5 w-5" data-id="element-936" />
            </div>
          </div>
          <div className="flex items-center text-sm font-medium text-amber-600" data-id="element-937">
            <AlertCircleIcon className="mr-1 h-4 w-4" data-id="element-938" />
            {pendingCount} requests awaiting approval
          </div>
        </div>
      </div>

      {/* Domestic vs International Split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-939">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-940">
          <div className="flex items-center gap-3 mb-4" data-id="element-941">
            <div className="h-10 w-10 rounded-full bg-navy-50 flex items-center justify-center" data-id="element-942">
              <HomeIcon className="h-5 w-5 text-navy-700" data-id="element-943" />
            </div>
            <div data-id="element-944">
              <h3 className="font-semibold text-navy-900" data-id="element-945">Domestic Travel</h3>
              <p className="text-xs text-slate-500" data-id="element-946">Within country</p>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2" data-id="element-947">
            <span className="text-sm text-slate-600" data-id="element-948">$198,000 of $280,000</span>
            <span className="text-sm font-bold text-navy-900" data-id="element-949">71%</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-950">
            <div className="h-full bg-navy-600 rounded-full" style={{
            width: '71%'
          }} data-id="element-951" />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-952">
          <div className="flex items-center gap-3 mb-4" data-id="element-953">
            <div className="h-10 w-10 rounded-full bg-gold-50 flex items-center justify-center" data-id="element-954">
              <GlobeIcon className="h-5 w-5 text-gold-600" data-id="element-955" />
            </div>
            <div data-id="element-956">
              <h3 className="font-semibold text-navy-900" data-id="element-957">
                International Travel
              </h3>
              <p className="text-xs text-slate-500" data-id="element-958">Cross-border</p>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2" data-id="element-959">
            <span className="text-sm text-slate-600" data-id="element-960">$164,000 of $300,000</span>
            <span className="text-sm font-bold text-navy-900" data-id="element-961">55%</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-962">
            <div className="h-full bg-gold-500 rounded-full" style={{
            width: '55%'
          }} data-id="element-963" />
          </div>
        </div>
      </div>

      {/* View Mode Toggle + Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-964">
        <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-965">
          <div className="border-b border-slate-200 px-6 py-4" data-id="element-966">
            <h3 className="font-semibold text-navy-900" data-id="element-967">Spend by Category</h3>
          </div>
          <div className="p-6 space-y-6" data-id="element-968">
            {categories.map(cat => <div key={cat.name} data-id="element-969">
                <div className="flex items-center justify-between mb-2" data-id="element-970">
                  <div className="flex items-center gap-3" data-id="element-971">
                    <div className={`p-2 rounded-lg ${cat.bg} ${cat.text}`} data-id="element-972">
                      <cat.icon className="h-4 w-4" data-id="element-973" />
                    </div>
                    <span className="font-medium text-slate-700" data-id="element-974">
                      {cat.name}
                    </span>
                  </div>
                  <div className="text-right" data-id="element-975">
                    <span className="font-bold text-navy-900" data-id="element-976">
                      ${cat.amount.toLocaleString()}
                    </span>
                    <span className="text-sm text-slate-500 ml-2" data-id="element-977">
                      ({cat.percent}%)
                    </span>
                  </div>
                </div>
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-978">
                  <div className={`h-full ${cat.color} rounded-full`} style={{
                width: `${cat.percent}%`
              }} data-id="element-979" />
                </div>
              </div>)}
          </div>
        </div>

        {/* Budget Configuration */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-980">
          <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center" data-id="element-981">
            <h3 className="font-semibold text-navy-900" data-id="element-982">Budget Limits</h3>
            <button className="text-xs font-medium text-navy-700 hover:underline flex items-center gap-1" data-id="element-983">
              <SettingsIcon className="h-3 w-3" data-id="element-984" /> Configure
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-985">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-986">
              <div className="flex items-center justify-between mb-1" data-id="element-987">
                <span className="text-sm font-medium text-slate-700" data-id="element-988">
                  Domestic Flight Cap
                </span>
                <span className="text-sm font-bold text-navy-900" data-id="element-989">
                  $500/ticket
                </span>
              </div>
              <p className="text-xs text-slate-500" data-id="element-990">Per person per booking</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-991">
              <div className="flex items-center justify-between mb-1" data-id="element-992">
                <span className="text-sm font-medium text-slate-700" data-id="element-993">
                  International Flight Cap
                </span>
                <span className="text-sm font-bold text-navy-900" data-id="element-994">
                  $1,500/ticket
                </span>
              </div>
              <p className="text-xs text-slate-500" data-id="element-995">Per person per booking</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-996">
              <div className="flex items-center justify-between mb-1" data-id="element-997">
                <span className="text-sm font-medium text-slate-700" data-id="element-998">
                  Hotel Nightly Cap
                </span>
                <span className="text-sm font-bold text-navy-900" data-id="element-999">
                  $300/night
                </span>
              </div>
              <p className="text-xs text-slate-500" data-id="element-1000">Standard room rate</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1001">
              <div className="flex items-center justify-between mb-1" data-id="element-1002">
                <span className="text-sm font-medium text-slate-700" data-id="element-1003">
                  Daily Meal Allowance
                </span>
                <span className="text-sm font-bold text-navy-900" data-id="element-1004">$75/day</span>
              </div>
              <p className="text-xs text-slate-500" data-id="element-1005">Per person per day</p>
            </div>
            <button onClick={() => setShowOverrideModal(true)} className="w-full rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100 transition-colors" data-id="element-1006">
              Request Budget Override
            </button>
          </div>
        </div>
      </div>

      {/* Department Utilization */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1007">
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center" data-id="element-1008">
          <div data-id="element-1009">
            <h3 className="font-semibold text-navy-900" data-id="element-1010">
              Department Budget Utilization
            </h3>
            <p className="text-xs text-slate-500 mt-1" data-id="element-1011">
              2 departments exceeding 75% threshold
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1012">
            {(['department', 'project', 'type'] as const).map(mode => <button key={mode} onClick={() => setViewMode(mode)} className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${viewMode === mode ? 'bg-navy-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1013">
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>)}
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-1014">
          <table className="w-full text-left text-sm" data-id="element-1015">
            <thead className="bg-slate-50 text-slate-500" data-id="element-1016">
              <tr data-id="element-1017">
                <th className="px-6 py-3 font-medium" data-id="element-1018">Department</th>
                <th className="px-6 py-3 font-medium" data-id="element-1019">Allocated</th>
                <th className="px-6 py-3 font-medium" data-id="element-1020">Spent</th>
                <th className="px-6 py-3 font-medium" data-id="element-1021">Remaining</th>
                <th className="px-6 py-3 font-medium" data-id="element-1022">Utilization</th>
                <th className="px-6 py-3 font-medium" data-id="element-1023">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-1024">
              {departments.map(dept => <tr key={dept.name} className="hover:bg-slate-50" data-id="element-1025">
                  <td className="px-6 py-4 font-medium text-navy-900" data-id="element-1026">
                    {dept.name}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-1027">
                    ${dept.budget.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-1028">
                    ${dept.spent.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-1029">
                    ${dept.remaining.toLocaleString()}
                  </td>
                  <td className="px-6 py-4" data-id="element-1030">
                    <div className="flex items-center gap-2" data-id="element-1031">
                      <span className="text-slate-700 w-8" data-id="element-1032">
                        {dept.percent}%
                      </span>
                      <div className="h-2 w-24 bg-slate-100 rounded-full overflow-hidden" data-id="element-1033">
                        <div className={`h-full rounded-full ${dept.color === 'emerald' ? 'bg-emerald-500' : dept.color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                      width: `${dept.percent}%`
                    }} data-id="element-1034" />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-1035">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${dept.status === 'On Track' ? 'bg-emerald-100 text-emerald-800' : dept.status === 'Warning' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-1036">
                      {dept.status === 'Critical' && <AlertCircleIcon className="mr-1 h-3 w-3" data-id="element-1037" />}
                      {dept.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Budget Alerts */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-1038">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2" data-id="element-1039">
          <h3 className="font-semibold text-navy-900" data-id="element-1040">Budget Alerts</h3>
          <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-1041">
            3
          </span>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-1042">
          {alerts.map((alert, i) => <div key={i} className={`p-4 flex items-start gap-4 hover:bg-slate-50 transition-colors ${alert.color}`} data-id="element-1043">
              <div className="mt-0.5" data-id="element-1044">
                <alert.icon className="h-5 w-5" data-id="element-1045" />
              </div>
              <div className="flex-1" data-id="element-1046">
                <h4 className="font-semibold text-sm" data-id="element-1047">{alert.title}</h4>
                <p className="text-sm opacity-90 mt-0.5" data-id="element-1048">{alert.desc}</p>
                <span className="text-xs opacity-75 mt-2 block" data-id="element-1049">
                  {alert.time}
                </span>
              </div>
              <button className="text-sm font-medium underline opacity-80 hover:opacity-100" data-id="element-1050">
                View Details
              </button>
            </div>)}
        </div>
      </div>

      {/* Override Modal */}
      {showOverrideModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-id="element-1051">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-id="element-1052">
            <div className="flex items-center justify-between mb-6" data-id="element-1053">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-1054">
                Budget Override Request
              </h3>
              <button onClick={() => setShowOverrideModal(false)} className="rounded-full p-1 hover:bg-slate-100" data-id="element-1055">
                <XIcon className="h-5 w-5 text-slate-500" data-id="element-1056" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-1057">
              <div data-id="element-1058">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1059">
                  Department
                </label>
                <select className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-1060">
                  <option data-id="element-1061">Product</option>
                  <option data-id="element-1062">Sales</option>
                  <option data-id="element-1063">Engineering</option>
                </select>
              </div>
              <div data-id="element-1064">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1065">
                  Override Amount
                </label>
                <div className="flex items-center gap-2" data-id="element-1066">
                  <span className="text-slate-500" data-id="element-1067">$</span>
                  <input type="number" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" placeholder="5000" data-id="element-1068" />
                </div>
              </div>
              <div data-id="element-1069">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1070">
                  Reason
                </label>
                <textarea rows={3} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" placeholder="Provide justification for the budget override..." data-id="element-1071" />
              </div>
              <p className="text-xs text-slate-500" data-id="element-1072">
                This override will be recorded in the audit log and requires
                finance approval.
              </p>
              <div className="flex gap-3 pt-2" data-id="element-1073">
                <button onClick={() => setShowOverrideModal(false)} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1074">
                  Cancel
                </button>
                <button onClick={() => setShowOverrideModal(false)} className="flex-1 rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-700" data-id="element-1075">
                  Submit Override
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}